import { Component, model } from '@angular/core';
import { langLabels, languages, type Language } from '@shared/models/auth';
import { DropdownModule } from '@tvd/design-system/dropdown';
import { Icon } from '@tvd/design-system/icon';

/**
 * Presentational language picker — a translate-icon trigger that opens a
 * dropdown of the available UI languages (by endonym). Purely two-way: the
 * `currentLang` model reflects the active language and is updated on
 * selection. Persistence (locale storage, user PATCH) belongs to the host,
 * which can react to the model change — the two call sites use different
 * auth services, so this component stays side-effect free.
 */
@Component({
  selector: 'tvd-language-picker',
  imports: [DropdownModule, Icon],
  template: `
    <button
      class="flex items-center gap-1 text-sm px-2 py-1 rounded-lg cursor-pointer hover:bg-background-200"
      tvdDropdownAnchor
      #langAnchor="tvdDropdownAnchor"
      (click)="langMenu.toggle()"
    >
      <tvd-icon icon="translate" class="text-sm" />
      <span>{{ langLabels[currentLang()] }}</span>
    </button>
    <tvd-dropdown #langMenu [anchor]="langAnchor">
      @for (lang of languages; track lang) {
        <button
          tvdDropdownItem
          class="w-full text-left px-3 py-1.5 cursor-pointer flex items-center justify-between gap-2"
          [class.font-semibold]="currentLang() === lang"
          (click)="select(lang); langMenu.close()"
        >
          <span>{{ langLabels[lang] }}</span>
          @if (currentLang() === lang) {
            <tvd-icon icon="check" class="text-sm" />
          }
        </button>
      }
    </tvd-dropdown>
  `,
})
export class LanguagePicker {
  readonly currentLang = model.required<Language>();

  readonly languages = languages;
  readonly langLabels = langLabels;

  select(lang: Language): void {
    if (lang === this.currentLang()) {
      return;
    }
    this.currentLang.set(lang);
  }
}
